const DEFAULT_CONCURRENCY = 3;
const DEFAULT_MAX_RETRIES = 3;

export default class ChunkVideoUploader {
  constructor(baseUrl, options = {}) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.maxConcurrency = options.maxConcurrency || DEFAULT_CONCURRENCY;
    this.maxRetries = options.maxRetries || DEFAULT_MAX_RETRIES;
    this.onProgress = options.onProgress || (() => {});
    this.uploadId = null;
    this.file = null;
    this.chunkSize = 0;
    this.totalChunks = 0;
    this.uploadedChunks = new Set();
    this.bytesUploaded = 0;
    this.paused = false;
    this.aborted = false;
    this.startTime = null;
    this.missingChunks = [];
  }

  async init(file) {
    const response = await fetch(`${this.baseUrl}/upload/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        fileSize: file.size,
        mimetype: file.type || "application/octet-stream",
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to initialize upload.");
    }

    const data = await response.json();
    this.uploadId = data.uploadId;
    this.chunkSize = data.chunkSize;
    this.totalChunks = data.totalChunks;
    this.file = file;
    this.uploadedChunks = new Set();
    this.bytesUploaded = 0;
    this.paused = false;
    this.aborted = false;
    this.missingChunks = [];

    await this.syncStatus();
    return data;
  }

  async getStatus() {
    if (!this.uploadId) return null;
    const response = await fetch(
      `${this.baseUrl}/upload/status/${this.uploadId}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch upload status.");
    }
    return response.json();
  }

  async syncStatus() {
    const status = await this.getStatus();
    if (!status) return status;

    if (Array.isArray(status.uploadedChunks)) {
      this.uploadedChunks = new Set(status.uploadedChunks);
      this.bytesUploaded = this.calculateBytesUploaded();
      if (this.file) {
        this.reportProgress();
      }
    }

    return status;
  }

  calculateBytesUploaded() {
    if (!this.file) return 0;

    let bytes = 0;
    this.uploadedChunks.forEach((index) => {
      const start = index * this.chunkSize;
      const end = Math.min(start + this.chunkSize, this.file.size);
      bytes += end - start;
    });
    return bytes;
  }

  getMissingChunkIndices() {
    const missing = [];
    for (let i = 0; i < this.totalChunks; i += 1) {
      if (!this.uploadedChunks.has(i)) {
        missing.push(i);
      }
    }
    return missing;
  }

  reportProgress() {
    if (!this.file) return;

    const totalBytes = this.file.size;
    const percent = totalBytes > 0 ? (this.bytesUploaded / totalBytes) * 100 : 0;
    const elapsed = this.startTime
      ? Math.max((Date.now() - this.startTime) / 1000, 0.001)
      : 0.001;
    const speedBytesPerSec = this.bytesUploaded / elapsed;
    const speedMbps = speedBytesPerSec / (1024 * 1024);
    const remainingBytes = Math.max(totalBytes - this.bytesUploaded, 0);
    const etaSeconds =
      speedBytesPerSec > 0 ? remainingBytes / speedBytesPerSec : null;

    this.onProgress({
      percent,
      bytesUploaded: this.bytesUploaded,
      totalBytes,
      speedMbps,
      etaSeconds,
      uploadedChunks: this.uploadedChunks.size,
      totalChunks: this.totalChunks,
      paused: this.paused,
      missingChunks: this.getMissingChunkIndices(),
    });
  }

  async uploadChunk(index) {
    const start = index * this.chunkSize;
    const end = Math.min(start + this.chunkSize, this.file.size);
    const chunk = this.file.slice(start, end);

    const formData = new FormData();
    formData.append("uploadId", this.uploadId);
    formData.append("chunkIndex", String(index));
    formData.append("chunk", chunk, `${this.file.name}.part${index}`);

    let lastError = null;
    for (let attempt = 0; attempt < this.maxRetries; attempt += 1) {
      if (this.aborted) {
        throw new Error("Upload cancelled.");
      }

      try {
        const response = await fetch(`${this.baseUrl}/upload/chunk`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Chunk ${index} upload failed.`);
        }

        const result = await response.json();
        if (Array.isArray(result.uploadedChunks)) {
          result.uploadedChunks.forEach((chunkIndex) => {
            this.uploadedChunks.add(chunkIndex);
          });
        } else {
          this.uploadedChunks.add(index);
        }

        this.bytesUploaded = this.calculateBytesUploaded();
        this.reportProgress();
        return result;
      } catch (error) {
        lastError = error;
        if (attempt < this.maxRetries - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * (attempt + 1))
          );
        }
      }
    }

    throw lastError || new Error(`Chunk ${index} upload failed.`);
  }

  async processQueue(pending) {
    const queue = [...pending];

    return new Promise((resolve, reject) => {
      const inFlight = new Set();

      const tryStartNext = () => {
        if (this.aborted) {
          resolve();
          return;
        }

        if (this.paused) {
          resolve();
          return;
        }

        while (inFlight.size < this.maxConcurrency && queue.length > 0) {
          const index = queue.shift();
          inFlight.add(index);

          this.uploadChunk(index)
            .then(() => {
              inFlight.delete(index);
              if (queue.length === 0 && inFlight.size === 0) {
                resolve();
              } else {
                tryStartNext();
              }
            })
            .catch((error) => {
              inFlight.delete(index);
              this.paused = true;
              this.missingChunks = this.getMissingChunkIndices();
              this.reportProgress();
              reject({
                error,
                missingChunks: this.missingChunks,
              });
            });
        }

        if (queue.length === 0 && inFlight.size === 0 && !this.paused) {
          resolve();
        }
      };

      tryStartNext();
    });
  }

  async uploadAll() {
    if (!this.file || !this.uploadId) {
      throw new Error("Upload is not initialized.");
    }

    this.startTime = Date.now();
    this.paused = false;
    this.aborted = false;
    this.missingChunks = [];

    const pending = this.getMissingChunkIndices();
    if (pending.length === 0) {
      return this.complete();
    }

    try {
      await this.processQueue(pending);
    } catch (pauseInfo) {
      throw pauseInfo;
    }

    if (this.paused || this.aborted) {
      return null;
    }

    return this.complete();
  }

  async resume() {
    if (!this.file || !this.uploadId) {
      throw new Error("No upload to resume.");
    }

    await this.syncStatus();
    this.paused = false;
    this.missingChunks = [];
    this.startTime = Date.now();

    const pending = this.getMissingChunkIndices();
    if (pending.length === 0) {
      return this.complete();
    }

    try {
      await this.processQueue(pending);
    } catch (pauseInfo) {
      throw pauseInfo;
    }

    if (this.paused || this.aborted) {
      return null;
    }

    return this.complete();
  }

  async complete() {
    const response = await fetch(`${this.baseUrl}/upload/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uploadId: this.uploadId }),
    });

    if (!response.ok) {
      throw new Error("Failed to complete upload.");
    }

    return response.json();
  }

  async cancel() {
    this.aborted = true;
    this.paused = false;

    if (!this.uploadId) return;

    try {
      await fetch(`${this.baseUrl}/upload/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId: this.uploadId }),
      });
    } catch (error) {
      console.error("Failed to cancel upload:", error);
    }
  }
}
